import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LocalCredentialsModel, WorkspaceModel, WorkspaceRepository } from '@database/central';
import { DeviceIdProvider } from '@database/workspace';
import { AuthProvider } from '@domain/authentication';
import { BusinessLayerService } from '@domain/business-layer';
import { LoginStepEnum, UserService } from '@domain/registration';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '@shared';

import {
	AlertService,
	AppRoutingKeys,
	ClipboardService,
	EncryptionService,
	PlatformName,
	PlatformProvider,
	ToastDuration,
	ToastService
} from '@shared';
import { Subscription, firstValueFrom, map } from 'rxjs';

@Component({
	selector: 'app-login',
	templateUrl: 'login.component.html',
	styleUrls: ['login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
	public usernameControl: UntypedFormControl;
	public pinControl: UntypedFormControl;
	public loginForm: UntypedFormGroup;
	public showPassword = false;
	public platform = this.platformProvider.getPlatform();
	public platformName = PlatformName;
	public loginStep = LoginStepEnum.username;
	public loginStepEnum = LoginStepEnum;
	public isNoApiModeEnabled: boolean;
	public apiStatus: any;
	private subscriptions: Subscription[] = [];

	public constructor(
		private navController: NavController,
		private toastService: ToastService,
		private translateService: TranslateService,
		private alertService: AlertService,
		private deviceIdProvider: DeviceIdProvider,
		private clipboardService: ClipboardService,
		private encryptionService: EncryptionService,
		private userService: UserService,
		private platformProvider: PlatformProvider,
		private authProvider: AuthProvider,
		private workspaceRepository: WorkspaceRepository,
		private storageService: StorageService,
		private businessLayerService: BusinessLayerService
	) {}

	public async ngOnInit() {
		this.usernameControl = new UntypedFormControl('', [Validators.required]);
		this.pinControl = new UntypedFormControl('', [
			Validators.minLength(4),
			Validators.maxLength(4),
			Validators.pattern('^[0-9]*$')
		]);

		this.loginForm = new UntypedFormGroup({
			usernameControl: this.usernameControl,
			pinControl: this.pinControl
		});

		this.subscriptions.push(
			this.businessLayerService
				.isNoApiModeEnabled$()
				.subscribe((isNoApi: boolean) => (this.isNoApiModeEnabled = isNoApi))
		);
	}

	public getActionButtonText(): string {
		const stepString =
			this.loginStep === this.loginStepEnum.username ? 'login.buttons.continue' : 'login.buttons.submit';
		return this.translateService.instant(stepString);
	}

	public toggleFieldTextType() {
		this.showPassword = !this.showPassword;
	}

	public goToRegistration(): void {
		this.navController.navigateForward(AppRoutingKeys.urlPathRegistration);
	}

	public goToResetPIN(): void {
		this.navController.navigateForward(AppRoutingKeys.urlPathResetPin);
	}

	public goBack(): void {
		this.loginForm.get('usernameControl').setValue('');
		this.loginForm.get('pinControl').setValue('');
		this.pinControl.removeValidators(Validators.required);
		this.pinControl.updateValueAndValidity();
		this.loginStep = this.loginStepEnum.username;
	}

	public async displayDeviceId() {
		const deviceId: string = await this.deviceIdProvider.getDeviceId();

		const copyButton = {
			text: this.translateService.instant('deviceId.alert.buttons.copy'),
			handler: async () => {
				await this.clipboardService.copy(deviceId);

				this.toastService.presentSuccess(
					this.translateService.instant('deviceId.toaster.messages.success'),
					ToastDuration.Fast
				);
			}
		};

		const closeButton = {
			text: this.translateService.instant('deviceId.alert.buttons.close'),
			handler: () => {}
		};

		this.alertService.presentAlert(
			`<strong>${this.translateService.instant('deviceId.alert.title')}</strong><br />
			${deviceId}`,
			[copyButton, closeButton],
			false
		);
	}

	public async onSubmit(formValues: { usernameControl: string; pinControl: string }): Promise<void> {
		if (!this.isFormValid()) {
			return;
		}

		if (this.loginStep === this.loginStepEnum.username) {
			this.pinControl.addValidators(Validators.required);
			this.pinControl.updateValueAndValidity();
			this.loginStep = this.loginStepEnum.pin;
			return;
		}

		await this.logOnNoApi()

		const userDetails: LocalCredentialsModel | null = await this.userService.getUserDetails(
			formValues.usernameControl
		);

		if (!userDetails || this.encryptionService.encrypt(formValues.pinControl) !== userDetails.pin) {
			this.loginForm.get('pinControl').setValue('', { onlyself: true });

			await this.toastService.presentError(
				this.translateService.instant('login.errorMessages.validation.invalidCredentials'),
				ToastDuration.Slow
			);

			return;
		}

		await this.authProvider.setFusionUserId(formValues.usernameControl);

		await firstValueFrom(
			this.workspaceRepository.getAllByFusionUserId$(formValues.usernameControl).pipe(
				map((workFiles: WorkspaceModel[]) => {
					const workFilesFiltered = workFiles
						.filter((workFile: WorkspaceModel) => !workFile.pendingDelete)
						.sort((a, b) => a.importedUnix - b.importedUnix);

					if (workFilesFiltered.length > 0) {
						// redirect to last imported workfile
						return this.navController.navigateRoot(
							`/${AppRoutingKeys.urlPathWorkfile}/${workFilesFiltered.pop().workFileId}`
						);
					}

					// redirect to import page
					return this.navController.navigateRoot(`/${AppRoutingKeys.urlPathImport}`);
				})
			)
		);
	}

	public isFormValid(): boolean {
		return this.loginForm.valid;
	}

	public async logOnNoApi(){
		this.apiStatus = await this.storageService.get("NO_API")
		if(this.apiStatus == null) {await this.storageService.set("NO_API", 0)}
		this.apiStatus = await this.storageService.get("NO_API")
		if(this.apiStatus == 1) {await this.storageService.set("NO_API", 0)}
		this.apiStatus = await this.storageService.get("NO_API")

		return this.apiStatus 
	}

	public noApiMode(): void {
		this.navController.navigateForward(`/${AppRoutingKeys.urlPathNoApiMode}`);
	}

	public ngOnDestroy(): void {
		this.subscriptions.forEach((subscription) => {
			subscription.unsubscribe();
		});
	}
}
